<template>
    <div class="homepage__first-block">
        <div class="first-block__wrap">
            <div class="first-block__content">
                <div class="content-wrapper">
                    <h1 class="content__title">
                        {{
                            isMobile
                                ? $t('lp.firstBlock.title')
                                : $t('lp.desktop.firstBlock.title')
                        }}
                    </h1>
                    <p class="content__description">
                        {{
                            isMobile
                                ? $t('lp.firstBlock.description')
                                : $t('lp.desktop.firstBlock.description')
                        }}
                    </p>
                    <div class="content__ctas">
                        <h3
                            v-html="
                                isMobile ? $t('lp.firstBlock.playForFree') : ''
                            "
                        />
                        <h3
                            class="sign-up"
                            v-html="
                                isMobile
                                    ? $t('lp.firstBlock.signUpAs')
                                    : $t('lp.desktop.firstBlock.signUpAs')
                            "
                        />
                        <SignUpButtons
                            class="buttons"
                            :display-parent="isMobile"
                            :display-student="isMobile"
                            :teacher-role-text="
                                currentLanguage === 'et' && !isMobile
                                    ? $t(
                                        'lp.desktop.firstBlock.teacherSignUpHere',
                                    )
                                    : null
                            "
                        />
                        <div v-if="isMobile" class="content__featured">
                            {{ $t('lp.firstBlock.featured') }}
                        </div>

                        <div
                            v-else
                            class="content__featured content__featured--clickable"
                            @click="signUp"
                        >
                            <u>{{ $t('lp.desktop.firstBlock.featured') }}</u>
                        </div>
                        <div class="social-proof">
                            <img
                                src="/images/lp/sp-logos--edsurge.svg"
                                alt="Edsurge"
                            />
                            <img
                                src="/images/lp/sp-logos--forbes.svg"
                                alt="Forbes"
                            />
                            <img src="/images/lp/sp-logos--bbc.svg" alt="BBC" />

                            <img
                                class="education-nation"
                                src="/images/lp/sp-logos--education-nation.svg"
                                alt="Education Nation"
                            />
                            <img
                                class="usc"
                                src="/images/lp/sp-logos--usc.svg"
                                alt="USC"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="first-block__video">
                <svg class="first-block__video-clip">
                    <clipPath id="firstBlockVideoClip">
                        <path
                            class="path-1"
                            d="M70.259 0H707.379C724.488 0 738.133 14.2862 737.347 31.3774L717.309 467.377C716.573 483.393 703.373 496 687.34 496H32.6958C15.1111 496 1.29708 480.945 2.80648 463.425L40.3697 27.4249C41.7062 11.912 54.6886 0 70.259 0Z"
                            fill="#C4C4C4"
                        />
                        <path
                            class="path-2"
                            d="M60.2651 0H534.597C551.706 0 565.351 14.2861 564.566 31.3773L549.859 351.377C549.122 367.393 535.923 380 519.89 380H32.6958C15.1111 380 1.29709 364.945 2.80649 347.425L30.3758 27.4249C31.7123 11.912 44.6947 0 60.2651 0Z"
                            fill="#C4C4C4"
                        />
                    </clipPath>
                </svg>
                <div class="first-block__video-wrap">
                    <iframe
                        src="https://player.vimeo.com/video/835799818?autoplay=1&loop=1&muted=1&player_id=0&amp;app_id=58479&background=1"
                        frameborder="0"
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowfullscreen
                        style="width: 100%; height: 100%"
                        title="99math"
                    />
                </div>
                <img
                    v-if="isTabletScreenWidth"
                    id="illustration"
                    src="/images/lp/hero--happy-kids.svg"
                    alt="Illustration"
                />
            </div>
        </div>
    </div>
</template>

<script>
import SignUpButtons from '../components/SignUpButtons.vue';
import { MobileScreenWidthDetect } from '@/core/mixins/MobileScreenWidthDetect';
import { supportWebPImage } from '@/core/helpers/utils';
import { mapGetters } from 'vuex';

export default {
    components: { SignUpButtons },
    mixins: [MobileScreenWidthDetect],
    data: function () {
        return {
            windowWidth: 0,
        };
    },
    computed: {
        ...mapGetters({ currentLanguage: 'getCurrentLanguage' }),
        isMobile() {
            return this.windowWidth <= 1024;
        },
    },
    watch: {
        isTabletScreenWidth() {
            this.changeImageIfNoWebPSupport();
            this.removeIllustrationIfVideoIsPlaying();
        },
    },
    unmounted() {
        window.removeEventListener('resize', this.onResize);
    },
    mounted() {
        this.windowWidth = window.innerWidth;

        window.addEventListener('resize', this.onResize);

        this.changeImageIfNoWebPSupport();

        this.$nextTick(() => {
            this.removeIllustrationIfVideoIsPlaying();
        });
    },
    methods: {
        signUp() {
            this.$router.push({ name: 'auth.register' });
        },
        onResize() {
            this.windowWidth = window.innerWidth;
        },
        changeImageIfNoWebPSupport() {
            if (!supportWebPImage()) {
                const videoTag = document.getElementById('hero-video');

                if (videoTag) {
                    videoTag.setAttribute('poster', '/images/lp/hero.png');
                }
            }
        },
        removeIllustrationIfVideoIsPlaying() {
            this.$nextTick(() => {
                const mobileHeroVideo = document.getElementById('hero-video');

                if (mobileHeroVideo) {
                    const illustration =
                        document.getElementById('illustration');

                    if (illustration) {
                        mobileHeroVideo.addEventListener('playing', () => {
                            illustration.style.display = 'none';
                        });
                    }
                }
            });
        },
    },
};
</script>

<style scoped lang="scss">
.homepage__first-block {
    background-color: #572598;
    position: relative;
    max-width: 2000px;
    margin: 0 auto;
    z-index: 0;

    @include mq($w-min-desktop, min) {
        flex-direction: row;
    }

    .first-block {
        &__wrap {
            box-sizing: border-box;
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            max-width: calc(1530px + 3rem);
            margin: 0 auto;
            padding: 4.5rem 1.5rem 5rem 1.5rem;

            @include mq($w-max-desktop, max) {
                max-width: calc(1280px + 3rem);
                padding-top: 4.125rem;
            }

            @include mq($w-max-tablet, max) {
                flex-direction: column;
                padding: 3rem 1.5rem;
            }

            @include mq($w-max-phone, max) {
                padding-top: 2.5rem;
                padding-bottom: 0;
            }
        }

        &__content {
            padding: 0 1rem 3rem 0;
            position: relative;
            flex: 1;
            z-index: 10;

            @include mq($w-min-md, min) {
                padding: 0 4rem 9rem 0;
                background: linear-gradient(
                    109.81deg,
                    #572598 52.66%,
                    rgba(87, 37, 152, 0) 87.24%
                );
            }

            .content {
                &__title {
                    font-style: normal;
                    font-weight: bold;
                    font-size: 1.75rem;
                    line-height: 130%;
                    text-align: center;
                    margin: 1rem 0;

                    @include mq($w-min-md, min) {
                        font-size: 3rem;
                        line-height: 130%;
                        text-align: left;
                        margin-bottom: 1rem;
                    }
                }

                &__description {
                    font-style: normal;
                    font-weight: normal;
                    font-size: 1.1rem;
                    line-height: 130%;
                    text-align: center;
                    margin: 1rem 0;

                    @include mq($w-min-md, min) {
                        font-size: 1.75rem;
                        line-height: 150%;
                        text-align: left;
                        margin-bottom: 3rem;
                    }
                }

                &__ctas {
                    box-sizing: border-box;
                    width: 100%;

                    h3 {
                        text-align: center;
                        margin: 2rem 0 0.2rem;
                        font-weight: 600;

                        @include mq($w-min-md, min) {
                            margin: 2rem 0 0.2rem;
                            font-size: 1.75rem;
                            text-align: left;
                        }
                    }

                    .sign-up {
                        font-weight: 500;
                        margin: 0.5rem 0 1rem;
                        font-size: 1.1rem;

                        @include mq($w-min-md, min) {
                            font-size: 22px;
                            margin: 0.5rem 0;
                        }
                    }

                    .buttons {
                        justify-content: flex-start;

                        .math-button {
                            min-height: 45px !important;

                            img {
                                max-height: 20px !important;
                            }
                        }
                    }

                    .social-proof {
                        margin: 1rem 0 2rem;
                        display: flex;
                        flex-wrap: wrap;
                        justify-content: center;
                        width: 100%;

                        @include mq($w-min-md, min) {
                            justify-content: flex-start;
                            margin: 1rem 0;
                        }

                        img {
                            max-width: 90px;
                            padding-right: 0.5rem;
                            object-fit: scale-down;
                            opacity: 0.7;
                        }

                        .education-nation {
                            max-width: 105px;
                        }

                        .usc {
                            max-width: 175px;

                            @include mq($w-min-md, min) {
                                max-width: 135px;
                            }
                        }
                    }
                }

                &__featured {
                    font-style: normal;
                    font-weight: normal;
                    font-size: 0.875rem;
                    line-height: 150%;
                    margin-top: 2rem;
                    text-align: center;
                    opacity: 0.8;

                    @include mq($w-min-md, min) {
                        text-align: left;
                        font-size: 1rem;
                        line-height: 130%;
                    }

                    &--clickable {
                        margin-top: 0;
                        margin-bottom: 2rem;

                        &:hover {
                            cursor: pointer;
                        }
                    }
                }
            }
        }

        &__video {
            position: relative;
            width: 738px;
            height: 496px;

            @include mq($w-max-desktop, max) {
                width: 566px;
                height: 380px;
            }

            @include mq($w-max-tablet, max) {
                order: 2;
                margin-top: 3rem;
            }

            @include mq($w-max-phone, max) {
                width: 100%;
                height: auto;
            }

            @include mq($w-max-phone, max) {
                margin-top: 0;
                margin-bottom: 4rem;
            }

            &:after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                height: 100%;
                width: 100%;

                @include mq($w-min-desktop, min) {
                    width: 60%;
                    background: none;
                }
            }

            img {
                top: 7%;
                position: absolute;
                z-index: 1;
                left: 0;
            }
        }

        &__video-clip {
            width: 0;
            height: 0;

            @include mq($w-max-phone, max) {
                display: none;
            }

            .path-1 {
                @include mq($w-max-desktop, max) {
                    display: none;
                }
            }

            .path-2 {
                display: none;

                @include mq($w-max-desktop, max) {
                    display: block;
                }
            }
        }

        &__video-wrap {
            width: 738px;
            height: 496px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            overflow: hidden;
            -webkit-clip-path: url(#firstBlockVideoClip);
            clip-path: url(#firstBlockVideoClip);

            @include mq($w-max-desktop, max) {
                width: 566px;
                height: 380px;
            }

            @include mq($w-max-phone, max) {
                position: static;
                transform: none;
                width: auto;
                height: auto;
                margin: 0 -1.5rem;
                -webkit-clip-path: none;
                clip-path: none;
            }

            video {
                width: auto;
                height: 100%;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);

                @include mq($w-max-phone, max) {
                    width: 100%;
                    height: auto;
                    position: static;
                    transform: none;
                    display: block;
                    line-height: 1;
                }
            }
        }
    }
}
</style>
<style lang="scss">
.homepage__first-block {
    button.math-button--teacher.math-button.math-button--primary.math-button--md {
        @include mq($w-min-desktop, min) {
            width: 320px;
        }
    }
}
</style>
